import { Home, ArrowLeft, Search, Mail, RefreshCw } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { useState } from "react";

const NotFoundPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const popularPages = [
    { name: "Home", path: "/" },
    { name: "Features", path: "/features" },
    { name: "Pricing", path: "/pricing" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // In a real app, you would implement search functionality
      // For now, we'll just redirect to home
      navigate("/");
    }
  };

  return (
    <Wrapper
      className="py-20 relative min-h-[80vh] flex items-center"
      data-oid=":qsurvu"
    >
      {/* Animated Background Elements */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        data-oid="gn8iqx0"
      >
        <div
          className="absolute top-1/4 left-1/4 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
          data-oid="y0u60zr"
        ></div>
        <div
          className="absolute bottom-1/3 right-1/3 w-24 h-24 bg-purple-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
          data-oid="hkmlb-c"
        ></div>
        <div
          className="absolute top-1/2 right-1/4 w-16 h-16 bg-pink-500/10 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "2s" }}
          data-oid="kl38bna"
        ></div>
      </div>

      <Container data-oid="3wxo:6u">
        <div className="text-center max-w-4xl mx-auto" data-oid="ow019g3">
          {/* 404 Hero */}
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-oid="dpho5mm"
          >
            <div
              className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4"
              data-oid="38hf-67"
            >
              404
            </div>
            <motion.h1
              className="text-3xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-oid="slhvh05"
            >
              Oops! Page Not Found
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              data-oid="q2zfane"
            >
              The page you're looking for seems to have vanished into the
              digital void. But don't worry, we'll help you find your way back
              to amazing AI-powered marketing!
            </motion.p>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            data-oid="oujgnwh"
          >
            <Link to="/" data-oid="4cipeai">
              <Button size="lg" className="px-8" data-oid="6jyjdpv">
                <Home className="mr-2 h-4 w-4" data-oid="hel19yc" />
                Go Home
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              className="px-8"
              onClick={() => navigate(-1)}
              data-oid="82zpke_"
            >
              <ArrowLeft className="mr-2 h-4 w-4" data-oid="n.b_m2t" />
              Go Back
            </Button>
            <Button
              size="lg"
              variant="ghost"
              className="px-8"
              onClick={() => window.location.reload()}
              data-oid="v:qjyl2"
            >
              <RefreshCw className="mr-2 h-4 w-4" data-oid="tm-fccm" />
              Refresh Page
            </Button>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            className="max-w-md mx-auto mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            data-oid="mlla1-l"
          >
            <form
              onSubmit={handleSearch}
              className="relative"
              data-oid="t:6gco1"
            >
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
                data-oid="fil8gqi"
              />
              <Input
                type="text"
                placeholder="Search our site..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-6 text-lg"
                data-oid="llyf9:b"
              />

              <Button
                type="submit"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
                data-oid="p:g48.1"
              >
                Search
              </Button>
            </form>
          </motion.div>

          {/* Popular Pages */}
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            data-oid="x-p7l69"
          >
            <h2 className="text-xl font-semibold mb-6" data-oid="elw.0br">
              Popular Pages
            </h2>
            <div
              className="flex flex-wrap justify-center gap-3"
              data-oid="3aqnfw1"
            >
              {popularPages.map((page, index) => (
                <motion.div
                  key={page.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 1.0 + index * 0.1 }}
                  data-oid="c.2c_.t"
                >
                  <Link to={page.path} data-oid="zvrf0-1">
                    <Button
                      variant="outline"
                      className="hover:bg-blue-50 hover:border-blue-200 transition-all duration-200"
                      data-oid="capugt7"
                    >
                      {page.name}
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Help Section */}
          <motion.div
            className="p-8 rounded-2xl border bg-gradient-to-r from-blue-50/50 to-purple-50/50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            data-oid="d9mxuls"
          >
            <h3 className="text-xl font-semibold mb-4" data-oid="4_fha3u">
              Still Can't Find What You're Looking For?
            </h3>
            <p className="text-muted-foreground mb-6" data-oid="duo0odu">
              Our support team is here to help you navigate and find exactly
              what you need.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              data-oid="ajzu9r5"
            >
              <Link to="/contact" data-oid="z09v2o2">
                <Button variant="outline" className="px-6" data-oid="y4-702i">
                  <Mail className="mr-2 h-4 w-4" data-oid="3aa5.rs" />
                  Contact Support
                </Button>
              </Link>
              <Button variant="ghost" className="px-6" data-oid="a50lrd0">
                <Search className="mr-2 h-4 w-4" data-oid=".tcxhlj" />
                View Sitemap
              </Button>
            </div>
          </motion.div>

          {/* Fun Message */}
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            data-oid="g6t49wc"
          >
            <p className="text-muted-foreground text-sm" data-oid="jc9gurc">
              🤖 Even our AI couldn't find that page, but it's working on better
              recommendations for you!
            </p>
          </motion.div>
        </div>
      </Container>

      {/* Floating Elements Animation */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        data-oid="a23.1lg"
      >
        <motion.div
          className="absolute top-1/4 left-10 w-4 h-4 bg-blue-500/30 rounded-full"
          animate={{
            y: [0, -20, 0],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          data-oid="ttfd5o0"
        />

        <motion.div
          className="absolute bottom-1/3 right-16 w-6 h-6 bg-purple-500/30 rounded-full"
          animate={{
            y: [0, 15, 0],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          data-oid="bxdqhrj"
        />

        <motion.div
          className="absolute top-1/2 right-1/4 w-3 h-3 bg-pink-500/40 rounded-full"
          animate={{
            y: [0, -10, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
          data-oid="y5e0p-p"
        />
      </div>
    </Wrapper>
  );
};

export default NotFoundPage;
